/**
 * character/animation.ts
 *
 * The animation state machine — because a character standing perfectly still
 * isn't really standing, they're frozen. Every transition has intention.
 * Every idle moment is a small story.
 *
 * Frame rates and timings here are tuned to feel like a person, not a puppet.
 */

import type { AnimState, Direction } from "./composer.js";

// Re-export these so consumers only need to import from animation.ts
export type { AnimState, Direction };

// ─── Configuration constants ──────────────────────────────────────────────────

/** Frames per second for each animation state */
const STATE_FPS: Record<AnimState, number> = {
  idle:        0.5,
  walk:        8,
  sit:         0.3,
  interact:    4,
  emote:       6,
  dance:       4,
  device:      2.5,
  device_walk: 5,    // slower than walk — distracted amble
  device_sit:  2,    // seated breathing with device
  combat_idle:   6,  // alert ready stance — bouncing on balls of feet
  combat_attack: 10, // fast strike — 3 frames at 10fps = 0.3s total
};

/** Total number of frames per animation state */
const STATE_FRAMES: Record<AnimState, number> = {
  idle:        4,
  walk:        8,
  sit:         4,
  interact:    4,
  emote:       6,
  dance:       8,
  device:      4,
  device_walk: 8,    // 8-frame walk cycle like normal walk
  device_sit:  4,    // seated tap cycle
  combat_idle:   4,  // 4-frame ready bounce loop
  combat_attack: 3,  // 3-frame strike, then auto-return to combat_idle
};

/**
 * How long to blend when switching between states (seconds).
 * Short enough to feel snappy, long enough to look intentional.
 */
const BLEND_DURATION: Record<AnimState, number> = {
  idle:        0.15,
  walk:        0.08,
  sit:         0.25,
  interact:    0.10,
  emote:       0.05,
  dance:       0.12,
  device:      0.12,
  device_walk: 0.10,  // quick blend from standing device pose
  device_sit:  0.15,
  combat_idle:   0.08,  // snap into stance quickly
  combat_attack: 0.03,  // nearly instant — strike should feel immediate
};

/** Walking vertical bob amplitude (sprite pixels — halved by 0.5× render scale) */
const WALK_BOB_AMPLITUDE = 3;

/** Dancing bob amplitude (slightly more expressive) */
const DANCE_BOB_AMPLITUDE = 4;

// ─── Direction utilities ──────────────────────────────────────────────────────

const DIRECTIONS: Direction[] = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];

/**
 * Map a movement vector to the nearest 8-direction.
 * dx/dy are screen-space deltas (positive y = down).
 */
export function vectorToDirection(dx: number, dy: number): Direction {
  if (dx === 0 && dy === 0) return "s";
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  // 0° = east, rotating clockwise. We remap to 8 directions.
  const normalized = ((angle + 360) % 360);
  const index = Math.round(normalized / 45) % 8;
  // atan2 gives us: 0=E, 45=SE, 90=S, 135=SW, 180=W, 225=NW, 270=N, 315=NE
  const map: Direction[] = ["e", "se", "s", "sw", "w", "nw", "n", "ne"];
  return map[index];
}

/**
 * Get the opposite direction (for facing toward someone).
 */
export function oppositeDirection(dir: Direction): Direction {
  const idx = DIRECTIONS.indexOf(dir);
  return DIRECTIONS[(idx + 4) % 8];
}

/**
 * Rotate a direction by N steps (positive = clockwise).
 */
export function rotateDirection(dir: Direction, steps: number): Direction {
  const idx = DIRECTIONS.indexOf(dir);
  return DIRECTIONS[((idx + steps) % 8 + 8) % 8];
}

/**
 * Angular distance between two directions in steps (0–4).
 */
export function directionDistance(a: Direction, b: Direction): number {
  const ia = DIRECTIONS.indexOf(a);
  const ib = DIRECTIONS.indexOf(b);
  const diff = Math.abs(ia - ib);
  return Math.min(diff, 8 - diff);
}

// ─── Idle variation system ────────────────────────────────────────────────────

/**
 * The tiny moments that make a character feel present:
 * a blink, a weight shift, a glance to the side.
 */
export type IdleVariation =
  | "blink"         // eyes close for one frame
  | "weight-shift"  // slight positional sway
  | "look-left"     // head turns left momentarily
  | "look-right"    // head turns right momentarily
  | "look-up"       // glances upward (curious)
  | "breathe"       // subtle rise (cosmetic only)
  | "none";         // just standing

interface IdleVariationConfig {
  duration: number;   // seconds this variation lasts
  cooldown: number;   // minimum seconds before can trigger again
}

const IDLE_VARIATION_CONFIG: Record<IdleVariation, IdleVariationConfig> = {
  "blink":        { duration: 0.15, cooldown: 2.5 },
  "weight-shift": { duration: 0.8,  cooldown: 4.0 },
  "look-left":    { duration: 0.6,  cooldown: 5.0 },
  "look-right":   { duration: 0.6,  cooldown: 5.0 },
  "look-up":      { duration: 0.5,  cooldown: 8.0 },
  "breathe":      { duration: 1.2,  cooldown: 3.0 },
  "none":         { duration: 0,    cooldown: 0   },
};

// ─── AnimationController class ────────────────────────────────────────────────

export interface AnimationSnapshot {
  state:        AnimState;
  direction:    Direction;
  frame:        number;
  isBlinking:   boolean;
  bobOffset:    number;
  weightOffset: number; // horizontal sway offset (pixels)
  lookOffset:   number; // head turn offset (used by composer)
}

export class AnimationController {
  // Current state
  private state: AnimState = "idle";
  private direction: Direction = "s";
  private frame: number = 0;
  private timer: number = 0;  // time since last frame advance

  // State transition blending
  private prevState: AnimState = "idle";
  private blendTimer: number = 0;
  private blendDuration: number = 0;

  // Direction smoothing (don't snap instantly)
  private targetDirection: Direction = "s";
  private directionHoldTimer: number = 0;
  private readonly DIRECTION_HOLD = 0.05; // seconds before we commit to a new direction

  // Idle variation system
  private currentVariation: IdleVariation = "none";
  private variationTimer: number = 0;
  private variationCooldowns: Map<IdleVariation, number> = new Map();
  private idleTimer: number = 0; // total time spent idle

  // Walk bob
  private bobPhase: number = 0;

  // Personality influence (set from personality.ts)
  blinkFrequency: number = 1.0;   // multiplier on blink cooldown
  fidgetLevel: number = 0.5;      // 0-1, how often idle variations trigger
  lookAroundBias: number = 0.5;   // 0-1, probability weight for look variations

  constructor(initialState: AnimState = "idle", initialDir: Direction = "s") {
    this.state = initialState;
    this.direction = initialDir;
    this.targetDirection = initialDir;
  }

  // ─── State management ──────────────────────────────────────────────────────

  /**
   * Transition to a new animation state.
   * Handles frame reset and blend setup internally.
   */
  setState(newState: AnimState): void {
    if (newState === this.state) return;

    this.prevState = this.state;
    this.state = newState;
    this.blendDuration = BLEND_DURATION[newState];
    this.blendTimer = this.blendDuration;

    // Reset frame on state change (except idle→sit which looks better continuing)
    if (!(this.prevState === "idle" && newState === "sit")) {
      this.frame = 0;
      this.timer = 0;
    }

    // Clear idle tracking when leaving idle
    if (newState !== "idle") {
      this.idleTimer = 0;
      this.currentVariation = "none";
      this.variationTimer = 0;
    }
  }

  getState(): AnimState {
    return this.state;
  }

  /**
   * Sync walk/device_walk animation to tile traversal progress.
   * When moveProgress is provided (0–1), the walk frame and bob phase
   * are driven directly by tile traversal — no timer-based animation.
   * This prevents skating: feet plant exactly when the character moves.
   */
  setMoveProgress(progress: number): void {
    if (this.state !== "walk" && this.state !== "device_walk") return;
    const frames = STATE_FRAMES[this.state];
    this.frame = Math.floor(progress * frames) % frames;
    // Bob: 2 bounces per gait cycle (one per step). Body rises at mid-stride
    // of each leg — phase tracks the frame so bob peaks at frames 2 and 6.
    this.bobPhase = progress * Math.PI * 2 * 2; // ×2 for double bounce
    // Mark that frame was externally driven so update() doesn't overwrite
    this._moveProgressDriven = true;
  }

  private _moveProgressDriven = false;

  /** True while blending from previous state */
  isBlending(): boolean {
    return this.blendTimer > 0;
  }

  getBlendAlpha(): number {
    if (this.blendDuration <= 0) return 1;
    return 1 - Math.max(0, this.blendTimer / this.blendDuration);
  }

  // ─── Direction management ──────────────────────────────────────────────────

  /**
   * Request a direction change. We hold briefly before committing,
   * preventing jitter when movement input is noisy.
   */
  setDirection(dir: Direction): void {
    if (dir === this.targetDirection) return;
    this.targetDirection = dir;
    this.directionHoldTimer = this.DIRECTION_HOLD;
  }

  /** Force an immediate direction change, no hold timer */
  setDirectionImmediate(dir: Direction): void {
    this.direction = dir;
    this.targetDirection = dir;
    this.directionHoldTimer = 0;
  }

  getDirection(): Direction {
    return this.direction;
  }

  // ─── Idle variations ───────────────────────────────────────────────────────

  private triggerVariation(variation: IdleVariation): void {
    const cfg = IDLE_VARIATION_CONFIG[variation];
    this.currentVariation = variation;
    this.variationTimer = cfg.duration;
    this.variationCooldowns.set(variation, cfg.cooldown);
  }

  private canTrigger(variation: IdleVariation): boolean {
    const cooldown = this.variationCooldowns.get(variation) ?? 0;
    return cooldown <= 0;
  }

  /**
   * Decide what idle variation to play next.
   * Uses probability weights shaped by personality settings.
   */
  private pickNextVariation(): IdleVariation {
    // Only trigger variations when idle for a moment
    if (this.idleTimer < 0.5) return "none";

    // Roll — more fidgety = more frequent triggers
    const triggerChance = 0.3 * this.fidgetLevel;
    if (Math.random() > triggerChance) return "none";

    // Weight table
    const candidates: Array<[IdleVariation, number]> = [
      ["blink",        3.0 * this.blinkFrequency],
      ["weight-shift", 1.5 * this.fidgetLevel],
      ["look-left",    2.0 * this.lookAroundBias],
      ["look-right",   2.0 * this.lookAroundBias],
      ["look-up",      0.8],
      ["breathe",      1.0],
    ];

    // Filter by cooldown
    const available = candidates.filter(([v]) => this.canTrigger(v));
    if (available.length === 0) return "none";

    // Weighted random pick
    const totalWeight = available.reduce((sum, [, w]) => sum + w, 0);
    let roll = Math.random() * totalWeight;
    for (const [variation, weight] of available) {
      roll -= weight;
      if (roll <= 0) return variation;
    }
    return available[available.length - 1][0];
  }

  // ─── Main update ───────────────────────────────────────────────────────────

  /**
   * Advance all timers and compute the current animation snapshot.
   * Call this once per frame with the elapsed time in seconds.
   */
  update(dt: number): void {
    // Direction hold timer
    if (this.directionHoldTimer > 0) {
      this.directionHoldTimer -= dt;
      if (this.directionHoldTimer <= 0) {
        this.direction = this.targetDirection;
      }
    }

    // Blend timer
    if (this.blendTimer > 0) {
      this.blendTimer = Math.max(0, this.blendTimer - dt);
    }

    // Frame advancement — skip if move-progress is driving the frame externally
    if (this._moveProgressDriven) {
      // Frame + bob were set by setMoveProgress(); clear flag for next tick
      this._moveProgressDriven = false;
    } else {
      const fps = STATE_FPS[this.state];
      const frameDuration = fps > 0 ? 1 / fps : Infinity;
      this.timer += dt;

      while (this.timer >= frameDuration && frameDuration < Infinity) {
        this.timer -= frameDuration;
        const nextFrame = (this.frame + 1) % STATE_FRAMES[this.state];

        // combat_attack auto-returns to combat_idle after playing through all frames
        if (this.state === "combat_attack" && nextFrame === 0) {
          this.setState("combat_idle");
          break;
        }

        this.frame = nextFrame;
      }
    }

    // Bob phase management:
    // - walk/device_walk: driven by setMoveProgress() — no time-based advancement
    // - dance/combat_idle: time-based phase advancement
    // - everything else: settle smoothly to zero
    const isWalkState = this.state === "walk" || this.state === "device_walk";
    if (this.state === "dance" || this.state === "combat_idle") {
      this.bobPhase += dt * STATE_FPS[this.state] * Math.PI;
    } else if (!isWalkState) {
      // Settle the bob gently when stopping
      if (Math.abs(this.bobPhase % (Math.PI * 2)) > 0.1) {
        this.bobPhase += dt * 8;
      }
    }
    // walk/device_walk: bobPhase is set externally by setMoveProgress, don't touch it here

    // Cooldown timers for idle variations
    for (const [variation, cooldown] of this.variationCooldowns) {
      const remaining = cooldown - dt;
      if (remaining <= 0) {
        this.variationCooldowns.delete(variation);
      } else {
        this.variationCooldowns.set(variation, remaining);
      }
    }

    // Idle variation logic
    if (this.state === "idle") {
      this.idleTimer += dt;

      if (this.currentVariation !== "none") {
        this.variationTimer -= dt;
        if (this.variationTimer <= 0) {
          this.currentVariation = "none";
        }
      } else {
        // Try to trigger something interesting
        const next = this.pickNextVariation();
        if (next !== "none") {
          this.triggerVariation(next);
        }
      }
    }

    if (this.state === "sit") {
      this.idleTimer += dt;
      // Sitting characters blink more rarely and never weight-shift
      if (this.currentVariation !== "none") {
        this.variationTimer -= dt;
        if (this.variationTimer <= 0) this.currentVariation = "none";
      } else if (this.canTrigger("blink") && Math.random() < 0.01 * dt * 60) {
        this.triggerVariation("blink");
      }
    }
  }

  // ─── Snapshot output ───────────────────────────────────────────────────────

  /**
   * Get the current animation state as a snapshot.
   * The composer reads this to decide what to draw.
   */
  getSnapshot(): AnimationSnapshot {
    const isBlinking = this.currentVariation === "blink";

    // Bob offset from walking, dancing, or combat ready stance
    let bobOffset = 0;
    if (this.state === "walk" || this.state === "device_walk") {
      bobOffset = Math.round(Math.sin(this.bobPhase) * WALK_BOB_AMPLITUDE);
    } else if (this.state === "dance") {
      bobOffset = Math.round(Math.sin(this.bobPhase) * DANCE_BOB_AMPLITUDE);
    } else if (this.state === "combat_idle") {
      // Alert ready bounce — slightly more than idle, less than dance
      bobOffset = Math.round(Math.sin(this.bobPhase) * (WALK_BOB_AMPLITUDE * 0.8));
    }

    // Weight shift — horizontal sway during idle
    let weightOffset = 0;
    if (this.currentVariation === "weight-shift") {
      const progress = this.variationTimer / IDLE_VARIATION_CONFIG["weight-shift"].duration;
      weightOffset = Math.round(Math.sin((1 - progress) * Math.PI) * 1);
    }

    // Look offset — conveys direction of attention
    let lookOffset = 0;
    if (this.currentVariation === "look-left") lookOffset = -1;
    else if (this.currentVariation === "look-right") lookOffset = 1;
    else if (this.currentVariation === "look-up") lookOffset = 2; // special value

    return {
      state:        this.state,
      direction:    this.direction,
      frame:        this.frame,
      isBlinking,
      bobOffset,
      weightOffset,
      lookOffset,
    };
  }

  // ─── Convenience queries ───────────────────────────────────────────────────

  /** Is the character currently playing a blink variation? */
  isBlinking(): boolean {
    return this.currentVariation === "blink";
  }

  /** Time spent in the current state (seconds) */
  getIdleTime(): number {
    return this.state === "idle" || this.state === "sit" ? this.idleTimer : 0;
  }

  /** Reset idle timer — call when a character starts interacting */
  resetIdleTimer(): void {
    this.idleTimer = 0;
  }

  /** Get current frame index */
  getFrame(): number {
    return this.frame;
  }

  /** Get current bob offset in pixels */
  getBobOffset(): number {
    return this.getSnapshot().bobOffset;
  }
}

// ─── Walk cycle helper ────────────────────────────────────────────────────────

/**
 * Given two isometric tile positions and elapsed time, return the
 * animation direction and frame for a character walking between them.
 *
 * This is a convenience for the room/movement system to use without
 * needing to understand animation internals.
 */
export function walkDirection(
  fromX: number, fromY: number,
  toX: number,   toY: number
): Direction {
  const dx = toX - fromX;
  const dy = toY - fromY;
  // In isometric space: screen directions map like this:
  // Moving in +wx is toward SE, +wy is toward SW
  const screenDX = (dx - dy) * 32; // isometric projection
  const screenDY = (dx + dy) * 16;
  return vectorToDirection(screenDX, screenDY);
}

// ─── Emote sequence helper ────────────────────────────────────────────────────

export interface EmoteSequence {
  name: string;
  durationSeconds: number;
  looping: boolean;
}

/** A catalog of emote types characters can perform */
export const EMOTE_CATALOG: Record<string, EmoteSequence> = {
  wave:    { name: "wave",    durationSeconds: 1.2, looping: false },
  dance:   { name: "dance",   durationSeconds: 4.0, looping: true  },
  sit:     { name: "sit",     durationSeconds: 0,   looping: true  }, // stays until cancelled
  bow:     { name: "bow",     durationSeconds: 1.0, looping: false },
  shrug:   { name: "shrug",   durationSeconds: 0.8, looping: false },
  clap:    { name: "clap",    durationSeconds: 1.5, looping: false },
  yawn:    { name: "yawn",    durationSeconds: 1.8, looping: false },
  stretch: { name: "stretch", durationSeconds: 2.0, looping: false },
  heart:   { name: "heart",   durationSeconds: 1.0, looping: false },
};
